import { useMutation, useQuery } from "@tanstack/react-query";
import {
  HelfoEngineService,
  type PostApiAdminHelfoEngineEvaluateResponses,
} from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";

export type BiometricMapEntry = {
  slug: string;
  biometricId: string | null;
  biometric: {
    id: string;
    name: string;
    shortName: string | null;
    unit: string | null;
    nlk: string | null;
    furstId: string | null;
  } | null;
};

export type MedicationKeywordEntry = {
  slug: string;
  keywords: string[];
};

// Derived from the generated OpenAPI response — single source of truth for
// the engine output shape. The codegen produces Record<string, unknown> for
// the two provenance maps (TypeBox→OpenAPI quirk), so we narrow them here.
type RawEvaluateResult =
  PostApiAdminHelfoEngineEvaluateResponses[200]["result"];

export type EvaluateResult = Omit<RawEvaluateResult, "privat" | "helfo"> & {
  privat: Omit<RawEvaluateResult["privat"], "provenance"> & {
    provenance: Record<string, string[]>;
  };
  helfo: Omit<RawEvaluateResult["helfo"], "panel_provenance"> & {
    panel_provenance: Record<string, string[]>;
  };
};

export type ClinicalInfoViolation =
  RawEvaluateResult["helfo"]["clinicalInfoViolations"][number];

export type DedupWarning = RawEvaluateResult["helfo"]["dedup_warnings"][number];

export const useHelfoBiometricMap = () =>
  useQuery({
    queryKey: ["helfo-engine", "biometric-map"],
    queryFn: async (): Promise<BiometricMapEntry[]> => {
      const res = await HelfoEngineService.getApiAdminHelfoEngineBiometricMap({
        headers: getAuthHeaders(),
      });
      const data = res.data as unknown as
        | { status: "success"; entries: BiometricMapEntry[] }
        | { status: "error"; message: string };
      if (!data || data.status !== "success") {
        throw new Error(
          data && "message" in data
            ? data.message
            : "Failed to load biometric map",
        );
      }
      return data.entries;
    },
    staleTime: 5 * 60_000,
  });

export const useHelfoMedicationKeywords = () =>
  useQuery({
    queryKey: ["helfo-engine", "medication-keywords"],
    queryFn: async (): Promise<MedicationKeywordEntry[]> => {
      const res =
        await HelfoEngineService.getApiAdminHelfoEngineMedicationKeywords({
          headers: getAuthHeaders(),
        });
      const data = res.data as unknown as
        | { status: "success"; entries: MedicationKeywordEntry[] }
        | { status: "error"; message: string };
      if (!data || data.status !== "success") {
        throw new Error(
          data && "message" in data
            ? data.message
            : "Failed to load medication keywords",
        );
      }
      return data.entries;
    },
    staleTime: 5 * 60_000,
  });

export type EngineAminoResponse = {
  amino_question_id: string;
  amino_alternative_ids?: string[];
  numeric_value?: number;
};

export type EngineAdapterOptions = {
  age?: number;
  sex_at_birth?: "male" | "female";
  overrides?: Record<string, unknown>;
};

export type EngineEvaluateBody = {
  responses: EngineAminoResponse[];
  options?: EngineAdapterOptions;
};

export const useEvaluateHelfoEngine = () =>
  useMutation({
    mutationFn: async (body: EngineEvaluateBody): Promise<EvaluateResult> => {
      const res = await HelfoEngineService.postApiAdminHelfoEngineEvaluate({
        body,
        headers: getAuthHeaders(),
      });
      const data = res.data as unknown as
        | { status: "success"; result: EvaluateResult }
        | { status: "error"; message: string };
      if (!data || data.status !== "success") {
        throw new Error(
          data && "message" in data ? data.message : "Engine call failed",
        );
      }
      return data.result;
    },
  });

/** "v1" is today's rules; "v2" is «Beslutningsstøtte for HELFO-rekvirering», September 2026. */
export type HelfoRulesVersion = "v1" | "v2";

export type LabVisitEvaluateResult = EvaluateResult & {
  /** Engine slug → known conditions that keep it off HELFO. Empty under v1. */
  knownConditionBlocks: Record<string, string[]>;
};

// DB-aware evaluation for an existing lab visit. Engine output is
// deterministic from inputs, so caching it keyed on labVisitId is safe —
// invalidate on survey-response or lab-visit changes that could affect the
// derived signals.
export const labVisitHelfoEvaluationQueryKey = (
  labVisitId: string,
  rules: HelfoRulesVersion,
) => ["helfo-engine", "lab-visit-evaluation", labVisitId, rules] as const;

export const labVisitHelfoEvaluationQueryFn =
  (labVisitId: string, rules: HelfoRulesVersion) =>
  async (): Promise<LabVisitEvaluateResult> => {
    const res =
      await HelfoEngineService.getApiAdminLabVisitsByIdHelfoEvaluation({
        path: { id: labVisitId },
        query: { rules },
        headers: getAuthHeaders(),
      });
    const data = res.data as unknown as
      | {
          status: "success";
          result: EvaluateResult;
          knownConditionBlocks: Record<string, string[]>;
        }
      | { status: "error"; message: string };
    if (!data || data.status !== "success") {
      throw new Error(
        data && "message" in data ? data.message : "Engine call failed",
      );
    }
    return { ...data.result, knownConditionBlocks: data.knownConditionBlocks };
  };

export const useLabVisitHelfoEvaluation = (
  labVisitId: string | undefined,
  rules: HelfoRulesVersion,
  options: { enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: labVisitHelfoEvaluationQueryKey(labVisitId ?? "", rules),
    queryFn: labVisitHelfoEvaluationQueryFn(labVisitId!, rules),
    enabled: Boolean(labVisitId) && options.enabled !== false,
    staleTime: 60_000,
  });

export const useValidateClinicalInfo = () =>
  useMutation({
    mutationFn: async (text: string): Promise<ClinicalInfoViolation[]> => {
      const res =
        await HelfoEngineService.postApiAdminHelfoEngineValidateClinicalInfo({
          body: { text },
          headers: getAuthHeaders(),
        });
      const data = res.data as unknown as
        | { status: "success"; violations: ClinicalInfoViolation[] }
        | { status: "error"; message: string };
      if (!data || data.status !== "success") {
        throw new Error(
          data && "message" in data ? data.message : "Validation call failed",
        );
      }
      return data.violations;
    },
  });
